"use client";

import { format } from "date-fns";
import { Clock } from "lucide-react";
import { Dispatch, SetStateAction, useMemo } from "react";
import { Link } from "react-router-dom";

import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
} from "@/components/ui/table";
import { PublicSession } from "@classview/shared-types";
import {
  PaginationState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { SessionRowActions } from "./sessions-row-actions";

interface SessionsTableProps {
  data: PublicSession[];
  pagination: PaginationState;
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>;
  pageCount?: number;
  isLoading?: boolean;
}

export function SessionsTable({
  data,
  pagination,
  onPaginationChange,
  pageCount,
  isLoading,
}: SessionsTableProps) {
  const columnHelper = createColumnHelper<PublicSession>();
  const columns = useMemo(
    () => [
      columnHelper.display({
        header: "Name",
        id: "name",
        cell: (info) => {
          return (
            <Link to={`/sessions/${info.row.original.id}`}>
              {info.row.original.name}
            </Link>
          );
        },
      }),
      columnHelper.display({
        header: "Date",
        id: "date",
        cell: (info) => {
          const sessionDuration = `${format(
            info.row.original.start,
            "HH:mm",
          )} - ${format(info.row.original.end, "HH:mm")}`;
          const formattedDate = format(info.row.original.start, "PPP");
          return (
            <div className="flex flex-col">
              <p className="text-sm text-muted-foreground">{sessionDuration}</p>
              <p className="text-sm text-muted-foreground">{formattedDate}</p>
            </div>
          );
        },
        size: 200,
      }),
      columnHelper.display({
        header: "Organiser",
        id: "organiser",
        cell: (info) => {
          return (
            <p className="text-sm text-muted-foreground">
              {info.row.original.organiser.firstName}{" "}
              {info.row.original.organiser.lastName}
            </p>
          );
        },
        size: 150,
      }),
      columnHelper.display({
        header: "Organisation",
        id: "organisation",
        cell: (info) => {
          const organisation = info.row.original.invitedOrganisations.find(
            (org) => org.id === info.row.original.hostOrganisationId,
          );

          return (
            <p
              className="truncate text-sm text-muted-foreground"
              title={organisation?.name}
            >
              {organisation?.name}
            </p>
          );
        },
        size: 110,
      }),
      columnHelper.display({
        id: "actions",
        cell: (info) => {
          return <SessionRowActions sessionId={info.row.original.id} />;
        },
        size: 70,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const table = useReactTable({
    data,
    columns,
    initialState: {
      pagination: {
        pageSize: pagination.pageSize,
        pageIndex: pagination.pageIndex,
      },
    },
    state: {
      pagination,
    },
    defaultColumn: {
      size: 999,
    },
    manualPagination: true,
    pageCount: pageCount,
    onPaginationChange: onPaginationChange,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow className="hover:bg-transparent" key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <TableHead
                  key={header.id}
                  style={{
                    width: header.getSize() === 999 ? "auto" : header.getSize(),
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && "selected"}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={columns.length} className="h-24 text-center">
              <p className="mb-2 flex flex-col items-center justify-center">
                <Clock className="size-6 text-secondary" />
              </p>
              <p className="font-semibold text-foreground">No sessions found</p>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      <TableFooter>
        <TablePagination table={table} isLoading={isLoading} />
      </TableFooter>
    </Table>
  );
}
